import { AlertCircle, Check, CheckSquare, FileSearch, Loader2, RefreshCw, X } from "lucide-react";
import * as React from "react";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { apiClient } from "@ando/core";
import { Badge, Button, cn } from "@ando/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { formatTimestamp } from "@/features/admin/lib/adminFormat";

import { HmEmpty, HmError, HmLoading } from "./HmStates";

/**
 * HmApprovalQueueTab — pending DRAFT prompt-version approval queue.
 *
 * Ported from v1 ``PromptApprovalQueueTab`` (Wave 7). Unblocks the MetaPrompt
 * loop: the agent generates DRAFT prompt versions from user feedback, an admin
 * approves/rejects them here. Only one ACTIVE version per specialist exists at
 * any time; approving a DRAFT deprecates the previous ACTIVE.
 *
 * The prompt endpoints are NOT tenant-scoped — ``tenantId`` is informational
 * only and surfaced for context.
 */

// ---------------------------------------------------------------------------
// Types — backend EvolutionLog entry. Extra keys are tolerated.
// ---------------------------------------------------------------------------

export interface EvolutionLogEntry {
  target_version_id: string;
  specialist_name: string;
  status?: string;
  decision: string; // e.g. "iterate", "rewrite", "deprecate"
  decision_reasoning: string;
  identified_issues: string[] | null;
  created_at: string;
  // Optional version/diff metadata, kept loose for forward-compat.
  version?: number | string;
  diff?: string;
  rationale?: string;
}

type ActionKind = "approve" | "reject";

const QUERY_KEY = ["admin", "hypermem", "approval-queue"] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function decisionBadgeVariant(decision: string): React.ComponentProps<typeof Badge>["variant"] {
  const norm = decision?.toLowerCase() ?? "";
  if (norm.includes("iterate") || norm.includes("update")) return "accent";
  if (norm.includes("rewrite") || norm.includes("major")) return "warning";
  if (norm.includes("deprecate") || norm.includes("reject")) return "destructive";
  if (norm.includes("approve") || norm.includes("ok")) return "success";
  return "outline";
}

const UNKNOWN_ERROR_MSG = msg`Errore sconosciuto`;

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

// ---------------------------------------------------------------------------
// Inline helper components
// ---------------------------------------------------------------------------

function IssuesPopover({
  issues,
}: {
  issues: string[] | null | undefined;
}): React.ReactElement {
  const { t } = useLingui();
  const [open, setOpen] = React.useState(false);
  const list = Array.isArray(issues) ? issues : [];
  if (!list.length) {
    return <span className="text-ink-3 text-xs">—</span>;
  }
  return (
    <div className="relative inline-block">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t`${list.length} issue identificati`}
      >
        <FileSearch className="h-3 w-3" aria-hidden />
        <span className="text-xs">
          <Trans>{list.length} issue</Trans>
        </span>
      </Button>
      {open && (
        <div
          className="absolute left-0 z-20 mt-1 max-h-72 w-80 overflow-auto rounded-xl border border-hairline bg-cream p-3 shadow-lg"
          role="dialog"
          aria-label={t`Issues identificati`}
        >
          <p className="mb-2 font-semibold text-ink-1 text-xs">
            <Trans>Issues identificati</Trans>
          </p>
          <ul className="list-disc space-y-1 pl-4 text-ink-2 text-xs">
            {list.map((iss, i) => (
              <li key={i}>{iss}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DecisionDialog({
  entry,
  action,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  entry: EvolutionLogEntry;
  action: ActionKind;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (payload: { note?: string; reason?: string }) => void;
}): React.ReactElement {
  const { t } = useLingui();
  const [note, setNote] = React.useState("");
  const [reason, setReason] = React.useState("");
  const isApprove = action === "approve";
  const confirmDisabled = pending || (!isApprove && !reason.trim());

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={isApprove ? t`Approva DRAFT` : t`Rigetta DRAFT`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !pending) onCancel();
      }}
    >
      <div className="w-full max-w-lg rounded-xl border border-hairline bg-cream p-5 shadow-xl">
        <h3 className="font-semibold text-base text-ink-1">
          {isApprove ? <Trans>Approva DRAFT</Trans> : <Trans>Rigetta DRAFT</Trans>}
        </h3>
        <p className="mt-1 text-ink-2 text-sm">
          {isApprove ? (
            <Trans>
              Attiva la nuova versione del prompt. La versione ATTIVA corrente per{" "}
              {entry.specialist_name ?? t`questo specialist`} verrà DEPRECATA.
            </Trans>
          ) : (
            <Trans>Deprecazione del draft. La motivazione sarà registrata nell'audit trail.</Trans>
          )}
        </p>

        {/* Diff / rationale context (read-only) */}
        {(entry.rationale ?? entry.decision_reasoning) && (
          <div className="mt-3 rounded-xl border border-hairline bg-cream/40 p-3">
            <p className="font-semibold text-ink-2 text-xs">
              <Trans>Motivazione</Trans>
            </p>
            <p className="mt-1 whitespace-pre-wrap text-ink-3 text-xs">
              {entry.rationale ?? entry.decision_reasoning}
            </p>
          </div>
        )}
        {entry.diff && (
          <pre className="mt-3 max-h-56 overflow-auto rounded-xl border border-hairline bg-cream/40 p-3 text-ink-2 text-xs">
            {entry.diff}
          </pre>
        )}

        <div className="mt-4">
          {isApprove ? (
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t`Nota opzionale (per audit)`}
              maxLength={500}
              aria-label={t`Nota opzionale per audit`}
              className="w-full rounded-xl border border-hairline bg-cream px-3 py-2 text-ink-1 text-sm placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ando-orange/45"
            />
          ) : (
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t`Motivo del rifiuto (richiesto)`}
              required
              rows={4}
              maxLength={1000}
              aria-label={t`Motivo del rifiuto`}
              className="w-full rounded-xl border border-hairline bg-cream px-3 py-2 text-ink-1 text-sm placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ando-orange/45"
            />
          )}
        </div>

        {error && (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-rose-300/70 bg-transparent p-3 text-rose-700 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{error}</span>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            <Trans>Annulla</Trans>
          </Button>
          <Button
            variant={isApprove ? "primary" : "destructive"}
            disabled={confirmDisabled}
            onClick={() =>
              onConfirm(
                isApprove
                  ? { note: note.trim() ? note.trim() : undefined }
                  : { reason: reason.trim() },
              )
            }
          >
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
            {pending ? <Trans>Invio…</Trans> : <Trans>Conferma</Trans>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function HmApprovalQueueTab({
  tenantId,
}: {
  tenantId: string | null;
}): React.ReactElement {
  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: QUERY_KEY,
    // BE returns an envelope ``{ entries: [...] }`` (prompt_management.py),
    // not a bare array — unwrap it here.
    queryFn: async () => {
      const res = await apiClient.get<{ entries: EvolutionLogEntry[] }>(
        "/admin/prompts/evolution-log?status=pending&limit=50",
      );
      return res.entries ?? [];
    },
  });

  const drafts = data ?? [];

  const [selected, setSelected] = React.useState<EvolutionLogEntry | null>(null);
  const [action, setAction] = React.useState<ActionKind | null>(null);

  const closeDialog = React.useCallback(() => {
    setSelected(null);
    setAction(null);
  }, []);

  const approveMutation = useMutation({
    mutationFn: (vars: { versionId: string; note?: string }) =>
      apiClient.post(`/admin/prompts/${encodeURIComponent(vars.versionId)}/approve`, {
        note: vars.note,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      closeDialog();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (vars: { versionId: string; reason: string }) =>
      apiClient.post(`/admin/prompts/${encodeURIComponent(vars.versionId)}/reject`, {
        reason: vars.reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      closeDialog();
    },
  });

  const activeMutation = action === "approve" ? approveMutation : rejectMutation;

  const handleConfirm = React.useCallback(
    (payload: { note?: string; reason?: string }) => {
      if (!selected) return;
      if (action === "approve") {
        approveMutation.reset();
        approveMutation.mutate({
          versionId: selected.target_version_id,
          note: payload.note,
        });
      } else if (action === "reject") {
        if (!payload.reason?.trim()) return;
        rejectMutation.reset();
        rejectMutation.mutate({
          versionId: selected.target_version_id,
          reason: payload.reason.trim(),
        });
      }
    },
    [selected, action, approveMutation, rejectMutation],
  );

  const openDialog = React.useCallback(
    (entry: EvolutionLogEntry, kind: ActionKind) => {
      approveMutation.reset();
      rejectMutation.reset();
      setSelected(entry);
      setAction(kind);
    },
    [approveMutation, rejectMutation],
  );

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-hairline bg-cream/40 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-ink-1 text-sm">
              <Trans>DRAFT prompt versions in attesa di approvazione</Trans>
            </h3>
            <p className="mt-1 text-ink-3 text-xs">
              <Trans>
                Il MetaPrompt agent genera proposte basate sui feedback degli utenti. Approva per
                attivare la nuova versione (1 sola attiva per specialist), rigetta per deprecare il
                draft.
              </Trans>
              {tenantId ? ` ${t`Tenant: ${tenantId}.`}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            className="flex shrink-0 items-center gap-1.5 text-ink-3 text-xs transition-colors hover:text-ink-1"
            aria-label={t`Aggiorna coda`}
          >
            <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} aria-hidden />
            <Trans>Aggiorna</Trans>
          </button>
        </div>

        <div className="mt-4">
          {isLoading ? (
            <HmLoading label={<Trans>Caricamento coda…</Trans>} />
          ) : isError ? (
            <HmError
              message={<Trans>Impossibile caricare la coda di approvazione</Trans>}
              onRetry={() => refetch()}
            />
          ) : drafts.length === 0 ? (
            <HmEmpty
              icon={<CheckSquare className="h-8 w-8 opacity-50" aria-hidden />}
              label={<Trans>Nessun DRAFT in attesa di approvazione</Trans>}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-hairline border-b text-ink-3 text-xs">
                    <th className="py-2 pr-3 font-medium">
                      <Trans>Specialist</Trans>
                    </th>
                    <th className="py-2 pr-3 font-medium">
                      <Trans>Decision</Trans>
                    </th>
                    <th className="py-2 pr-3 font-medium">
                      <Trans>Reasoning</Trans>
                    </th>
                    <th className="py-2 pr-3 font-medium">
                      <Trans>Issues</Trans>
                    </th>
                    <th className="py-2 pr-3 font-medium">
                      <Trans>Created</Trans>
                    </th>
                    <th className="py-2 pl-3 text-right font-medium">
                      <Trans>Azioni</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hairline">
                  {drafts.map((d) => (
                    <tr key={d.target_version_id}>
                      <td className="py-2.5 pr-3 align-top">
                        <Badge variant="accent">{d.specialist_name}</Badge>
                      </td>
                      <td className="py-2.5 pr-3 align-top">
                        <Badge variant={decisionBadgeVariant(d.decision)}>
                          {d.decision || "—"}
                        </Badge>
                      </td>
                      <td
                        className="max-w-md truncate py-2.5 pr-3 align-top text-ink-2 text-xs"
                        title={d.decision_reasoning}
                      >
                        {d.decision_reasoning}
                      </td>
                      <td className="py-2.5 pr-3 align-top">
                        <IssuesPopover issues={d.identified_issues} />
                      </td>
                      <td className="whitespace-nowrap py-2.5 pr-3 align-top text-ink-3 text-xs">
                        {formatTimestamp(d.created_at)}
                      </td>
                      <td className="whitespace-nowrap py-2.5 pl-3 text-right align-top">
                        <div className="inline-flex gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => openDialog(d, "approve")}
                          >
                            <Check className="h-3 w-3" aria-hidden />
                            <Trans>Approva</Trans>
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openDialog(d, "reject")}>
                            <X className="h-3 w-3" aria-hidden />
                            <Trans>Rigetta</Trans>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected && action && (
        <DecisionDialog
          entry={selected}
          action={action}
          pending={activeMutation.isPending}
          error={
            activeMutation.isError
              ? errorMessage(activeMutation.error, i18n._(UNKNOWN_ERROR_MSG))
              : null
          }
          onCancel={closeDialog}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}

export default HmApprovalQueueTab;
